import { acquireIdToken, DEV_BYPASS_AUTH, renovarSesion } from "./auth";

async function authHeaders(): Promise<Record<string, string>> {
  if (DEV_BYPASS_AUTH) return {}; // dev: el backend usa DEV_USER_EMAIL
  const token = await acquireIdToken();
  return { Authorization: `Bearer ${token}` };
}

/** fetch autenticado; si el backend contesta 401 la sesión murió → al login. */
async function fetchAuth(url: string, init: RequestInit = {}): Promise<Response> {
  const auth = await authHeaders();
  const res = await fetch(url, {
    credentials: "include",
    ...init,
    headers: { ...auth, ...(init.headers as Record<string, string> | undefined) },
  });
  if (res.status === 401) await renovarSesion();
  return res;
}

async function errorDetail(res: Response): Promise<string> {
  let detail = `${res.status} ${res.statusText}`.trim();
  try {
    const j = await res.json();
    if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
  } catch {
    // ignore
  }
  return detail;
}

async function jsonFetch<T>(url: string): Promise<T> {
  const res = await fetchAuth(url);
  if (!res.ok) throw new Error(await errorDetail(res));
  return res.json();
}

async function send<T>(
  url: string,
  method: "POST" | "PUT" | "DELETE",
  body?: unknown,
): Promise<T | null> {
  const res = await fetchAuth(url, {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(await errorDetail(res));
  if (res.status === 204) return null;
  return res.json();
}

export type Empleado = {
  email: string;
  nombre: string | null;
  jefe_email: string | null;
  activo: boolean;
  dias_bolsa: number;
  fecha_ingreso: string | null; // YYYY-MM-DD
  empresa: string;
};

export type Vacacion = {
  id: number;
  empleado_email: string;
  empleado_nombre: string | null;
  fecha_inicio: string; // YYYY-MM-DD
  fecha_fin: string; // YYYY-MM-DD
  tipo: string;
  comentario: string | null;
  estatus: string; // pendiente | autorizado | rechazado | baja_pendiente
  fuera_politica: boolean; // capturada después del cierre de la quincena anterior al goce
  autorizado_por: string | null;
  autorizado_en: string | null;
  creado_en: string;
  actualizado_en: string;
  /** Solo en la respuesta de autorizar: si el formato PDF se envió a RH. */
  formato_rh_enviado?: boolean | null;
};

/** Un abono de la bolsa. Siempre se cumple dias = usados + restantes + vencidas. */
export type BolsaDetalle = {
  asignada: string;   // YYYY-MM-DD en que se asignó
  vence: string;      // YYYY-MM-DD en que vence (18 meses después)
  dias: number;
  usados: number;
  restantes: number;
  vencidas: number;   // días que se perdieron al vencer (0 si sigue vigente)
  vigente: boolean;
};

export type Saldo = {
  fecha_ingreso: string | null;
  anio_antiguedad: number | null;
  bolsa: number;   // días de las bolsas vigentes hoy
  usados: number;  // consumidos de esas bolsas vigentes
  saldo: number;   // bolsa - usados
  pendientes: number;              // restantes de la bolsa anterior (la que vence primero)
  pendientes_vence: string | null; // YYYY-MM-DD en que vencen esos días
  usados_totales: number;    // incluye lo consumido de bolsas ya vencidas
  vencidas_totales: number;  // días perdidos al vencer
  detalle: BolsaDetalle[];
  proximo_abono: string | null; // YYYY-MM-DD del siguiente aniversario
  proximo_abono_dias: number | null;
};

export type VacacionInput = {
  fecha_inicio: string;
  fecha_fin: string;
  tipo: string;
  comentario: string | null;
};

export function getMe(): Promise<{
  email: string;
  name: string;
  dias_bolsa: number;
  jefe_email: string | null;
  es_jefe: boolean;
  /** Administrador (SYNC_ADMINS): ve también las bajas lógicas de equipo. */
  es_admin: boolean;
}> {
  return jsonFetch("/api/me");
}

export function getSaldo(): Promise<Saldo> {
  return jsonFetch("/api/saldo");
}

export function getVacacionesPendientes(): Promise<Vacacion[]> {
  return jsonFetch("/api/vacaciones/pendientes");
}

export async function autorizarVacacion(id: number): Promise<Vacacion> {
  return (await send<Vacacion>(`/api/vacaciones/${id}/autorizar`, "POST")) as Vacacion;
}
export async function rechazarVacacion(id: number): Promise<Vacacion> {
  return (await send<Vacacion>(`/api/vacaciones/${id}/rechazar`, "POST")) as Vacacion;
}
export async function autorizarBaja(id: number): Promise<void> {
  await send(`/api/vacaciones/${id}/autorizar-baja`, "POST");
}
export async function rechazarBaja(id: number): Promise<Vacacion> {
  return (await send<Vacacion>(`/api/vacaciones/${id}/rechazar-baja`, "POST")) as Vacacion;
}

/** Sin argumento: solo MPP (vistas de vacaciones). `"todas"`: incluye Pant3ra. */
export function getEmpleados(empresa?: string): Promise<Empleado[]> {
  const qs = empresa ? `?empresa=${encodeURIComponent(empresa)}` : "";
  return jsonFetch(`/api/empleados${qs}`);
}

export type DiaFestivo = { fecha: string; descripcion: string };

export function getDiasFestivos(): Promise<DiaFestivo[]> {
  return jsonFetch("/api/dias-festivos");
}

export function getVacaciones(
  opts: { anio?: number; mes?: number; empleado?: string } = {},
): Promise<Vacacion[]> {
  const p = new URLSearchParams();
  if (opts.anio != null) p.set("anio", String(opts.anio));
  if (opts.mes != null) p.set("mes", String(opts.mes));
  if (opts.empleado) p.set("empleado", opts.empleado);
  const qs = p.toString();
  return jsonFetch(`/api/vacaciones${qs ? `?${qs}` : ""}`);
}

export async function crearVacacion(v: VacacionInput): Promise<Vacacion> {
  return (await send<Vacacion>("/api/vacaciones", "POST", v)) as Vacacion;
}

export async function actualizarVacacion(id: number, v: VacacionInput): Promise<Vacacion> {
  return (await send<Vacacion>(`/api/vacaciones/${id}`, "PUT", v)) as Vacacion;
}

export async function borrarVacacion(id: number): Promise<{ resultado: string }> {
  return (await send<{ resultado: string }>(`/api/vacaciones/${id}`, "DELETE")) as {
    resultado: string;
  };
}

// ---------- Equipos / responsivas ----------

export const TIPOS_EQUIPO = [
  "Laptop",
  "Computadora Personal",
  "Dock",
  "Monitor",
  "Teclado",
  "Mouse",
  "Auriculares",
] as const;

export const EMPRESAS = ["MPP ENERGIA", "PANT3RA INFRAESTRUCTURA"] as const;

export type Equipo = {
  id: number;
  empleado_email: string;
  empleado_nombre: string | null;
  tipo: string;
  marca: string;
  modelo: string;
  numero_serie: string;
  caracteristicas: string;
  incluye_cargador: boolean;
  empresa: string;
  creado_en: string;
  actualizado_en: string;
};

/** La empresa no se envía: el backend la toma de las propiedades del empleado. */
export type EquipoInput = {
  empleado_email: string;
  tipo: string;
  marca: string;
  modelo: string;
  numero_serie: string;
  caracteristicas: string;
  incluye_cargador: boolean;
};

export function getEquipos(empleado?: string): Promise<Equipo[]> {
  const qs = empleado ? `?empleado=${encodeURIComponent(empleado)}` : "";
  return jsonFetch(`/api/equipos${qs}`);
}

export async function crearEquipo(e: EquipoInput): Promise<Equipo> {
  return (await send<Equipo>("/api/equipos", "POST", e)) as Equipo;
}

export async function actualizarEquipo(id: number, e: EquipoInput): Promise<Equipo> {
  return (await send<Equipo>(`/api/equipos/${id}`, "PUT", e)) as Equipo;
}

export async function borrarEquipo(id: number): Promise<void> {
  await send(`/api/equipos/${id}`, "DELETE");
}

/** Dispara la descarga del cuerpo de `res` con el nombre del Content-Disposition
 * (o `fallback` si el backend no lo mandó). */
async function descargarBlob(res: Response, fallback: string): Promise<void> {
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const disp = res.headers.get("Content-Disposition") ?? "";
  // Preferir filename* (UTF-8, conserva acentos); si no, el filename ASCII.
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disp);
  const ascii = /filename="([^"]+)"/.exec(disp);
  let nombre = fallback;
  if (utf8) {
    try {
      nombre = decodeURIComponent(utf8[1]);
    } catch {
      nombre = ascii ? ascii[1] : fallback;
    }
  } else if (ascii) {
    nombre = ascii[1];
  }
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Descarga la responsiva (docx) del empleado (empresa: la del empleado). */
export async function descargarResponsiva(empleado: string): Promise<void> {
  const p = new URLSearchParams({ empleado });
  const res = await fetchAuth(`/api/equipos/responsiva?${p}`);
  if (!res.ok) throw new Error(await errorDetail(res));
  await descargarBlob(res, `FORMATO RESPONSIVA  EQUIPOS  CORPORATIVO - ${empleado}.docx`);
}

/** Transfiere el equipo a otro usuario: baja lógica en el origen + alta en el destino.
 *  Devuelve el equipo nuevo (ya del destino). */
export async function transferirEquipo(
  id: number,
  destino_email: string,
  motivo: string,
): Promise<Equipo> {
  return (await send<Equipo>(`/api/equipos/${id}/transferir`, "POST", {
    destino_email,
    motivo,
  })) as Equipo;
}

// ---------- Bajas de asignación (lógica / física) ----------

/** `logica`: solo se quita de la lista (captura errónea, reasignación).
 *  `fisica`: el usuario entrega el equipo → queda en "Bajas de asignación" + formato PDF. */
export type TipoBaja = "logica" | "fisica";

export type BajaEquipo = {
  id: number;
  equipo_id: number;
  empleado_email: string;
  empleado_nombre: string | null;
  tipo_baja: TipoBaja;
  motivo: string;
  tipo: string;
  marca: string;
  modelo: string;
  numero_serie: string;
  caracteristicas: string;
  incluye_cargador: boolean;
  empresa: string;
  asignado_en: string;
  registrado_por: string;
  registrado_en: string;
};

export async function darDeBajaEquipo(
  id: number,
  tipo_baja: TipoBaja,
  motivo: string,
): Promise<{ resultado: string; tipo_baja: TipoBaja; baja_id: number | null }> {
  return (await send(`/api/equipos/${id}/baja`, "POST", { tipo_baja, motivo })) as {
    resultado: string;
    tipo_baja: TipoBaja;
    baja_id: number | null;
  };
}

export function getBajasEquipo(
  empleado: string,
  tipo: TipoBaja | "todas" = "fisica",
): Promise<BajaEquipo[]> {
  const p = new URLSearchParams({ empleado, tipo });
  return jsonFetch(`/api/equipos/bajas?${p}`);
}

/** Deshace una baja: el equipo vuelve a la lista del usuario. */
export async function restaurarBajaEquipo(id: number): Promise<Equipo> {
  return (await send<Equipo>(`/api/equipos/bajas/${id}/restaurar`, "POST")) as Equipo;
}

/** Descarga el formato "Baja de asignación de equipo" (PDF) para firma. */
export async function descargarFormatoBaja(id: number, fallback = "baja-equipo.pdf"): Promise<void> {
  const res = await fetchAuth(`/api/equipos/bajas/${id}/formato`);
  if (!res.ok) throw new Error(await errorDetail(res));
  await descargarBlob(res, fallback);
}

// ---------- Firma (imagen para los formatos) ----------

export type FirmaEstado = {
  tiene_firma: boolean;
  mime: string | null;
  actualizado_en: string | null;
};

export function getFirma(): Promise<FirmaEstado> {
  return jsonFetch("/api/firma");
}

/** Blob de la imagen de la firma del usuario (para preview), o null si no tiene. */
export async function getFirmaImagen(): Promise<Blob | null> {
  const res = await fetchAuth("/api/firma/imagen");
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await errorDetail(res));
  return res.blob();
}

export async function subirFirma(file: File): Promise<{ ok: boolean }> {
  const fd = new FormData();
  fd.append("file", file);
  // Sin Content-Type: el browser pone el boundary del multipart.
  const res = await fetchAuth("/api/firma", { method: "PUT", body: fd });
  if (!res.ok) throw new Error(await errorDetail(res));
  return res.json();
}

export async function borrarFirma(): Promise<void> {
  await send("/api/firma", "DELETE");
}

/** (Re)envía a RH por correo el formato PDF de unas vacaciones autorizadas
 * ("Vacaciones de X AUTORIZADAS"). Botón ✈ de Mis vacaciones. */
export async function enviarFormatoVacacion(
  id: number,
): Promise<{ resultado: string; destinatario?: string }> {
  return (await send(`/api/vacaciones/${id}/enviar-formato`, "POST")) as {
    resultado: string;
    destinatario?: string;
  };
}

/** Descarga el formato "Solicitud y Autorización de Vacaciones" (PDF) del periodo. */
export async function descargarFormatoVacacion(id: number, fechaInicio: string): Promise<void> {
  const res = await fetchAuth(`/api/vacaciones/${id}/formato`);
  if (!res.ok) throw new Error(await errorDetail(res));
  await descargarBlob(res, `Solicitud_Vacaciones_${fechaInicio}.pdf`);
}
